/**
 * autoSync.ts
 * バックグラウンド自動同期マネージャー
 * - 同時実行ロック（二重送信防止）
 * - 完了リスナー（UI更新通知）
 * - エラーは全てキャッチしてUIに伝播させない
 */

import { processQueue, getPendingCount } from './syncQueue';
import { getUnsyncedTimestamps, markTimestampsSynced } from '../services/offlineDb';
import { faceLogin, postTimestampWithToken } from '../services/api';
import { getStoredFaceSecret } from '../hooks/useFaceAuth';
import { getOfflineSecret, offlineAuthReady } from './offlineAuth';

type ApiFetch = (path: string, opts?: RequestInit) => Promise<unknown>;

let _apiFetch: ApiFetch | null = null;

/**
 * キュー項目を送信する。user_id があればその本人の顔シークレットでトークンを取得して送信し、
 * 共有端末でも打刻が正しい本人に帰属するようにする。取得できない場合は現在セッションで送信。
 * トークンはこの同期ラン内でユーザー別にキャッシュする。
 */
function buildPostItem(): (body: Record<string, unknown>, userId?: string) => Promise<unknown> {
  const tokenCache = new Map<string, string | null>();
  return async (body, userId) => {
    if (userId) {
      let token = tokenCache.get(userId);
      if (token === undefined) {
        token = null;
        await offlineAuthReady();
        const secret = getOfflineSecret(userId) ?? getStoredFaceSecret(userId);
        if (secret) {
          try { token = (await faceLogin(userId, secret)).access_token; }
          catch { token = null; }
        }
        tokenCache.set(userId, token);
      }
      if (token) { await postTimestampWithToken(token, body); return; }
    }
    // フォールバック: 現在のセッショントークンで送信
    if (!_apiFetch) throw new Error('apiFetch not initialized');
    await _apiFetch('/timestamps', { method: 'POST', body: JSON.stringify(body) });
  };
}
let _isSyncing = false;
const _listeners = new Set<(result: { synced: number; failed: number; pending: number }) => void>();

/**
 * IndexedDB の未送信レコードをサーバーと照合して送信する。
 * TimestampPage がオフライン時に IndexedDB へ保存したレコードを
 * オンライン復帰後に自動でサーバーへ送信する。
 */
async function reconcileIndexedDB(): Promise<void> {
  if (!_apiFetch) return;
  try {
    const records = await getUnsyncedTimestamps();
    if (records.length === 0) return;

    const syncedIds: number[] = [];
    for (const record of records) {
      try {
        await _apiFetch('/timestamps', {
          method: 'POST',
          body: JSON.stringify({
            timestamp_type:   record.timestampType,
            recorded_at:      record.recordedAt,
            local_id:         record.localId,
            latitude:         record.latitude  ?? undefined,
            longitude:        record.longitude ?? undefined,
            vessel_id:        record.vesselId  ?? undefined,
            session_id:       record.workSessionId ?? undefined,
            work_type:        record.workType  ?? undefined,
            note:             record.note      ?? undefined,
          }),
        });
        if (record.id !== undefined) syncedIds.push(record.id);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        // 4xx 系（セッション消滅・バリデーションエラー）は送信不可のため済みとしてマーク
        const is4xx = /セッションが見つかりません|既に出勤中|vessel_id|BAD_REQUEST|NOT_FOUND|HTTP 4/.test(msg);
        if (is4xx && record.id !== undefined) {
          syncedIds.push(record.id);
        } else {
          break; // ネットワークエラー/5xx → 次回に持ち越し
        }
      }
    }
    if (syncedIds.length > 0) {
      await markTimestampsSynced(syncedIds);
      console.log(`[autoSync] IndexedDB reconcile: ${syncedIds.length}件 同期完了`);
    }
  } catch (e) {
    console.warn('[autoSync] reconcileIndexedDB failed:', e);
  }
}

/** apiFetch を登録（App起動時に一度だけ呼ぶ） */
export function initAutoSync(apiFetch: ApiFetch) {
  _apiFetch = apiFetch;

  // オンライン復帰時に IndexedDB 未送信レコードを照合・送信
  window.addEventListener('online', () => {
    reconcileIndexedDB().catch(() => {});
    triggerSync().catch(() => {});
  });
}

/** IndexedDB 照合を外部から手動トリガーする */
export async function triggerReconcile(): Promise<void> {
  return reconcileIndexedDB();
}

/** 同期完了リスナーを登録。戻り値は解除関数 */
export function onSyncComplete(
  cb: (result: { synced: number; failed: number; pending: number }) => void
): () => void {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

/** 同期を実行する（多重実行ガード付き） */
export async function triggerSync(): Promise<void> {
  if (!_apiFetch || _isSyncing) return;

  // キューが空なら何もしない
  if (getPendingCount() === 0) return;

  _isSyncing = true;
  try {
    const result = await processQueue(buildPostItem());
    const pending = getPendingCount();
    _listeners.forEach(cb => {
      try { cb({ ...result, pending }); } catch { /* リスナーエラーは無視 */ }
    });
  } catch {
    // ネットワークエラー等は無視（次の周期で再試行）
  } finally {
    _isSyncing = false;
  }
}

/** 同期中かどうか */
export function isSyncing(): boolean {
  return _isSyncing;
}
